import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { TimeAgoBadge } from "components/common/TimeAgoBadge";
import TruncatedText from "components/common/TruncatedText";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getCustomersList } from "store/slices/customersSlice";
import {
  getMembershipLogsList,
  resetFilters,
  setFilter,
} from "store/slices/membershipLogsSlice";
import { getPlansList } from "store/slices/membershipSlice";
import {
  MEMBERSHIP_EVENTS_OPTIONS,
  ORDER_BY_OPTIONS,
  PERFORMER_ROLES,
} from "utils/constants";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";

const MembershipLogs = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();  const { noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView } = canAction("membership_logs");
  const [customerOptions, setCustomerOptions] = useState([]);

  const {
    items,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: {
      keyword,
      dates,
      status,
      orderBy,
      actionType,
      userId,
      membershipId,
      performedByRole,
    },
  } = useSelector((state) => state.MembershipLogs);
  const { customersList } = useSelector((state) => state.Customers);
  const plansList = useSelector((state) => state.Membership.plansList);

  useEffect(() => {
    if (customersList?.length) {
      setCustomerOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));

        customersList.forEach(({ _id, name, phone }) => {
          map.set(_id, {
            name: `${phone} ${name || ""}`,
            value: _id,
          });
        });

        return Array.from(map.values());
      });
    }
  }, [customersList]);

  useEffect(() => {
    if (canView) {
      getAllData();
      dispatch(getCustomersList(""));
      dispatch(getPlansList());
    }
  }, [canView]);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      performedByRole: isReset ? "" : performedByRole,
      action: isReset ? "" : actionType,
      userId: isReset ? "" : userId,
      membershipId: isReset ? "" : membershipId,
    };

    dispatch(getMembershipLogsList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "performedByRole",
      placeholder: t("Performed By"),
      type: "singleSelect",
      options: PERFORMER_ROLES,
    },
    {
      key: "actionType",
      placeholder: t("Action"),
      type: "singleSelect",
      options: MEMBERSHIP_EVENTS_OPTIONS,
    },
    {
      key: "userId",
      placeholder: t("Customer"),
      type: "singleSelect",
      options: customerOptions,
    },
    {
      key: "membershipId",
      placeholder: t("Membership"),
      type: "singleSelect",
      options: formatOptionsForSelect(plansList, language),
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Timestamp"),
        key: "createdAt",
        render: (value) => (
          <div>
            <TimeAgoBadge date={value} />
            <div className="ltr_text">{formatUTCDate(value)}</div>
          </div>
        ),
      },
      {
        header: t("Customer"),
        key: "userId",
        render: (value) =>
          value?.name?.trim() ? (
            <Link to={`/customers/customer-details?id=${value._id}`}>
              <p>{value?.name}</p>
              <p>{value?.phone}</p>
            </Link>
          ) : (
            "-"
          ),
      },
      {
        header: t("Action"),
        key: "action",
        render: (value) => (
          <span className={`membership_log_badge ${getActionClass(value)}`}>
            {membershipEventMap[value] || value || "-"}
          </span>
        ),
      },
      {
        header: t("Action By"),
        key: "performedBy",
        render: (value) => (
          <div className="">
            {value?.role === "system" ? (
              <>
                <p className="role text-capitalize">By {value?.role}</p>
              </>
            ) : (
              <>
                <p className="name">{value?.name || "-"}</p>
                <p className="role">({value?.role || "-"})</p>
              </>
            )}
          </div>
        ),
      },
      {
        header: t("Summery"),
        key: "summary",
        render: (value) => (
          <div className="summery">
            <span>{value}</span>
          </div>
        ),
      },
    ],
    [t],
  );
  const membershipEventMap = Object.fromEntries(
    MEMBERSHIP_EVENTS_OPTIONS.map((item) => [item.value, item.name]),
  );
  const getActionClass = (action) => {
    if (!action) return "action-badge";

    // FAILED (highest priority)
    if (action.includes("FAILED")) return "badge-failed";

    // SUCCESS / COMPLETED
    if (
      action.includes("SUCCESS") ||
      action.includes("COMPLETED") ||
      action.includes("ACTIVATED") ||
      action.includes("RENEWED") ||
      action.includes("ADDED") ||
      action.includes("RECEIVED")
    )
      return "badge-success";

    // CANCEL
    if (action.includes("CANCEL") || action.includes("CANCELLED"))
      return "badge-cancel";

    // PAYMENT / REFUND
    if (action.includes("PAYMENT") || action.includes("REFUND"))
      return "badge-payment";

    // UPGRADE / DOWNGRADE
    if (action.includes("UPGRADE") || action.includes("DOWNGRADE"))
      return "badge-upgrade";

    // PAUSE / RESUME
    if (action.includes("PAUSE") || action.includes("RESUMED"))
      return "badge-pause";

    // TRANSFER / ADDON / REPEAT
    if (
      action.includes("TRANSFERRED") ||
      action.includes("ADDON") ||
      action.includes("REPEATED")
    )
      return "badge-info";

    // REQUEST / INITIATED
    if (action.includes("REQUESTED") || action.includes("INITIATED"))
      return "badge-warning";

    return "action-badge";
  };

  const renderActions = useCallback(
    (item) => (
      <>
        {canView && (
          <Link to={`/membership-logs/details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
        )}
      </>
    ),
    [canView, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <h4 className="page_title mb-3">{t("Membership Logs")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search ")}
        filters={filtersList}
        selectedFilters={{
          keyword,
          status,
          orderBy,
          performedByRole,
          actionType,
          userId,
        }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />

      {loading && <TableShimmer rows={7} columns={8} />}

      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No Membership Logs Found")} icon={noPlans} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default MembershipLogs;
